/**
 * Baut die VBA-Prüfmakros, die der Kurs zum Einfügen anbietet.
 *
 * Jedes Prüfmakro besteht aus einem festen Rahmen und einem kurzen Rumpf, der
 * die eigentliche Angabe ausliest. Der Rahmen ist für alle Anwendungen gleich
 * und kümmert sich um drei Dinge:
 *
 * 1. Er sammelt die ausgelesenen Werte über `Wert`, `Zahl` und `JaNein`.
 *    Zahlen und Wahrheitswerte werden dabei sprachunabhängig geschrieben –
 *    `CStr(1.5)` ergäbe im deutschen Office „1,5“, `CStr(True)` sogar „Wahr“.
 * 2. Er legt den Bericht als benutzerdefinierten XML-Teil in der Datei ab und
 *    ersetzt dabei einen älteren Bericht derselben Prüfung. Den Teil liest die
 *    Prüffunktion über Office.js (siehe `pruefbericht.ts`).
 * 3. Er fängt Fehler ab und schreibt sie ebenfalls in den Bericht, damit die
 *    Rückmeldung im Panel sagen kann, was schiefgegangen ist.
 *
 * Am Dokument selbst ändert ein Prüfmakro nichts – es liest nur.
 */

import type { MacroSnippet, OfficeHost } from "@/core/types";

/** Namensraum der Prüfberichte. Muss mit `pruefbericht.ts` übereinstimmen. */
export const PRUEFBERICHT_NAMESPACE = "urn:interaktiv-office-course:pruefbericht";

/** Das Objekt, über das VBA die geöffnete Datei erreicht. */
const DOKUMENT: Record<OfficeHost, string> = {
  Word: "ActiveDocument",
  Excel: "ActiveWorkbook",
  PowerPoint: "ActivePresentation",
};

const DATEI: Record<OfficeHost, string> = {
  Word: "das Dokument",
  Excel: "die Arbeitsmappe",
  PowerPoint: "die Präsentation",
};

export interface PruefmakroDefinition {
  /** Kennung des Berichts, z. B. "pp-uebergaenge". Nur Kleinbuchstaben, Ziffern und Bindestriche. */
  pruefung: string;
  host: OfficeHost;
  /** Kurzer Titel für Panel und Kopfkommentar. */
  titel: string;
  /**
   * VBA-Zeilen, die die Angaben auslesen. Zur Verfügung stehen
   * `Wert "name", text`, `Zahl "name", zahl` und `JaNein "name", wahrheitswert`.
   *
   * Variablen im Rumpf dürfen nicht so heißen wie die Hilfsprozeduren – VBA
   * unterscheidet keine Groß- und Kleinschreibung, `Dim zahl` bricht also
   * `Zahl` – und nicht wie VBA-Anweisungen (`Name`, `Open`, `Line` …).
   */
  rumpf: string[];
  /** Zusätzliche Hilfsfunktionen, die der Rumpf braucht (ganze Prozeduren). */
  hilfsfunktionen?: string[];
}

/** Name der Prozedur, wie er im Makro-Dialog erscheint. */
export function makroName(pruefung: string): string {
  return `Pruefmakro_${pruefung.replace(/[^A-Za-z0-9]/g, "_")}`;
}

function einruecken(zeilen: string[], tiefe = 1): string {
  const abstand = "    ".repeat(tiefe);
  return zeilen.map((zeile) => (zeile.length > 0 ? abstand + zeile : zeile)).join("\n");
}

/** Erzeugt das vollständige Prüfmakro samt Rahmen. */
export function pruefmakro(definition: PruefmakroDefinition): MacroSnippet {
  if (!/^[a-z0-9-]+$/.test(definition.pruefung)) {
    throw new Error(`Ungültige Kennung für ein Prüfmakro: ${definition.pruefung}`);
  }

  const name = makroName(definition.pruefung);
  const dok = DOKUMENT[definition.host];

  const code = [
    "' ================================================================",
    `' Prüfmakro: ${definition.titel}`,
    "' Interaktive Office-Kurse",
    "'",
    `' Liest ${DATEI[definition.host]} aus und legt das Ergebnis als Prüfbericht`,
    "' in der Datei ab. Am Inhalt selbst ändert das Makro nichts.",
    "'",
    "' So geht's:",
    "'   1. Alt + F11 öffnet den VBA-Editor",
    "'      (Mac: Extras > Makro > Visual Basic-Editor).",
    "'   2. Einfügen > Modul und diesen Code in das leere Modul einfügen.",
    `'   3. Den Cursor in "Sub ${name}" setzen und F5 drücken.`,
    "' ================================================================",
    "",
    "Private Bericht As String",
    "",
    `Public Sub ${name}()`,
    "    On Error GoTo Fehler",
    '    Bericht = ""',
    "",
    einruecken(definition.rumpf),
    "",
    `    BerichtSpeichern "${definition.pruefung}"`,
    '    MsgBox "Der Prüfbericht ist gespeichert." & vbCr & vbCr & _',
    '        "Wechsle zurück ins Kurs-Panel und klicke dort auf ""Aufgabe prüfen"".", _',
    '        vbInformation, "Interaktive Office-Kurse"',
    "    Exit Sub",
    "",
    "Fehler:",
    "    Dim meldung As String",
    "    meldung = Err.Description",
    "    ' Erst den laufenden Fehler löschen – sonst greift Resume Next im Handler nicht.",
    "    On Error GoTo -1",
    "    On Error Resume Next",
    '    Wert "fehler", meldung',
    `    BerichtSpeichern "${definition.pruefung}"`,
    '    MsgBox "Beim Auslesen ist ein Fehler aufgetreten:" & vbCr & meldung, _',
    '        vbExclamation, "Interaktive Office-Kurse"',
    "End Sub",
    "",
    ...(definition.hilfsfunktionen ?? []).flatMap((funktion) => [funktion.trim(), ""]),
    "Private Sub Wert(ByVal schluessel As String, ByVal inhalt As String)",
    '    Bericht = Bericht & "<wert name=""" & XmlText(schluessel) & """>" & XmlText(inhalt) & "</wert>"',
    "End Sub",
    "",
    "Private Sub Zahl(ByVal schluessel As String, ByVal inhalt As Double)",
    "    ' Str() schreibt immer einen Punkt als Dezimaltrennzeichen.",
    "    Wert schluessel, Trim$(Str$(inhalt))",
    "End Sub",
    "",
    "Private Sub JaNein(ByVal schluessel As String, ByVal inhalt As Boolean)",
    '    If inhalt Then Wert schluessel, "1" Else Wert schluessel, "0"',
    "End Sub",
    "",
    "Private Function XmlText(ByVal s As String) As String",
    "    Dim i As Integer",
    "    For i = 0 To 31",
    '        If i <> 9 And i <> 10 And i <> 13 Then s = Replace(s, Chr$(i), " ")',
    "    Next i",
    '    s = Replace(s, "&", "&amp;")',
    '    s = Replace(s, "<", "&lt;")',
    '    s = Replace(s, ">", "&gt;")',
    '    s = Replace(s, """", "&quot;")',
    "    XmlText = s",
    "End Function",
    "",
    "Private Sub BerichtSpeichern(ByVal pruefung As String)",
    `    Const NS As String = "${PRUEFBERICHT_NAMESPACE}"`,
    "    Dim i As Long",
    `    For i = ${dok}.CustomXMLParts.Count To 1 Step -1`,
    `        If ${dok}.CustomXMLParts(i).NamespaceURI = NS Then`,
    `            If InStr(${dok}.CustomXMLParts(i).XML, "pruefung=""" & pruefung & """") > 0 Then`,
    `                ${dok}.CustomXMLParts(i).Delete`,
    "            End If",
    "        End If",
    "    Next i",
    `    ${dok}.CustomXMLParts.Add "<pruefbericht xmlns=""" & NS & """ pruefung=""" & pruefung & _`,
    '        """ version=""1"" erstellt=""" & Format$(Now, "yyyy-mm-dd\\Thh:nn:ss") & """>" & _',
    '        Bericht & "</pruefbericht>"',
    "End Sub",
    "",
  ].join("\n");

  return {
    pruefung: definition.pruefung,
    host: definition.host,
    name,
    titel: definition.titel,
    code,
  };
}
